import React from "react";
import styled from "styled-components";

// Styles
import colors from "styles/colors";

const SheetOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: flex-end;
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
`;

const SheetContainer = styled.div`
  background: ${colors.background.white};
  width: 500px;
  height: 100vh;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);
  transform: translateX(${props => props.isOpen ? '0' : '100%'});
  transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  display: flex;
  flex-direction: column;
  will-change: transform;
`;

const SheetHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid ${colors.neutral[200]};
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-shrink: 0;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const SheetTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.dark};
  margin: 0 0 8px 0;
  line-height: 1.3;
`;

const SurveyMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const MetaBadge = styled.span`
  background: ${props => props.primary ? colors.primary[50] : colors.neutral[200]};
  color: ${props => props.primary ? colors.primary[600] : colors.text.gray};
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  border: ${props => props.primary ? `1px solid ${colors.primary[100]}` : 'none'};
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const UseTemplateButton = styled.button`
  background-color: ${colors.primary[500]};
  color: ${colors.background.white};
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background-color: ${colors.primary[600]};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const EditButton = styled.button`
  background-color: ${colors.neutral[100]};
  color: ${colors.text.dark};
  border: 1px solid ${colors.neutral[300]};
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background-color: ${colors.neutral[200]};
    border-color: ${colors.neutral[400]};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: ${colors.text.gray};
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  flex-shrink: 0;
  
  &:hover {
    background: ${colors.neutral[200]};
  }
`;

const SheetContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 24px 24px;
`;

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.text.dark};
  margin: 0 0 12px 0;
`;

const SectionText = styled.p`
  font-size: 14px;
  color: ${colors.text.gray};
  margin: 0;
  line-height: 1.6;
`;

const AuthorSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: ${colors.background.lightBlue};
  border-radius: 8px;
  margin-bottom: 24px;
`;

const AuthorAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${colors.primary[500]}, ${colors.primary[600]});
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  font-weight: 600;
  flex-shrink: 0;
`;

const AuthorInfo = styled.div`
  flex: 1;
`;

const AuthorName = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.text.dark};
  margin: 0 0 4px 0;
`;

const AuthorTitle = styled.p`
  font-size: 12px;
  color: ${colors.text.gray};
  margin: 0;
`;

const QuestionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const QuestionItem = styled.div`
  padding: 16px;
  background: ${colors.background.lightBlue};
  border-radius: 8px;
  border-left: 4px solid ${colors.primary[500]};
`;

const QuestionText = styled.p`
  font-size: 14px;
  color: ${colors.text.dark};
  margin: 0 0 8px 0;
  font-weight: 500;
`;

const QuestionType = styled.span`
  font-size: 11px;
  color: ${colors.text.gray};
  text-transform: uppercase;
  font-weight: 600;
`;

const UsageSection = styled.div`
  background: ${colors.neutral[200]};
  padding: 16px;
  border-radius: 8px;
`;

const UsageTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.dark};
  margin: 0 0 8px 0;
`;

const UsageText = styled.p`
  font-size: 13px;
  color: ${colors.text.gray};
  margin: 0;
  line-height: 1.5;
`;

const SurveyDetailSheet = ({ isOpen, onClose, survey, onUseTemplate, onEdit, canEdit = false }) => {
  const getAuthorInitials = (name) => {
    if (!name) return "IL";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getQuestionTypeDisplay = (question) => {
    if (question.type === "text" || question.type?.value === "text") {
      return "Open Text";
    }
    if (question.type === "scale" || question.type?.value === "scale") {
      return `${question.scale || "0-10"} Scale`;
    }
    return "Multiple Choice";
  };

  const getUsageGuidance = (type) => {
    const guidance = {
      Retention:
        "Best used quarterly or bi-annually to track employee satisfaction trends and identify early warning signs of turnover risk.",
      Culture:
        "Deploy after major organizational changes, during culture transformation initiatives, or annually as part of culture assessment programs.",
      Leadership:
        "Ideal for post-change communications, leadership development programs, or when implementing new management structures.",
      Onboarding:
        "Schedule 30-60 days after employee start date to capture initial experience while it's fresh and actionable.",
    };
    return (
      guidance[type] ||
      "Use as needed based on your organizational goals and timing requirements."
    );
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Always render the component, but control visibility with CSS
  return (
    <SheetOverlay isOpen={isOpen} onClick={handleOverlayClick}>
      <SheetContainer isOpen={isOpen}>
        {survey && (
          <>
            <SheetHeader>
              <HeaderContent>
                <SheetTitle>{survey.title}</SheetTitle>
                <SurveyMeta>
                  <MetaBadge primary>
                    {survey.questions.length} question
                    {survey.questions.length !== 1 ? "s" : ""}
                  </MetaBadge>
                  <MetaBadge>{survey.type}</MetaBadge>
                </SurveyMeta>
                <ButtonContainer>
                  {canEdit && (
                    <EditButton onClick={() => onEdit(survey)}>
                      Edit
                    </EditButton>
                  )}
                  <UseTemplateButton onClick={() => onUseTemplate(survey)}>
                    Use Template
                  </UseTemplateButton>
                </ButtonContainer>
              </HeaderContent>
              <CloseButton onClick={onClose}>×</CloseButton>
            </SheetHeader>

            <SheetContent>
              {survey?.creator ? (
                <AuthorSection>
                  <AuthorAvatar>
                    {getAuthorInitials(survey?.creator)}
                  </AuthorAvatar>
                  <AuthorInfo>
                    <AuthorName>{survey?.creator}</AuthorName>
                  </AuthorInfo>
                </AuthorSection>
              ) : (
                <AuthorSection>
                  <AuthorAvatar>
                    {getAuthorInitials("Dr. Danielle Prowse")}
                  </AuthorAvatar>
                  <AuthorInfo>
                    <AuthorName>Dr. Danielle Prowse</AuthorName>
                    <AuthorTitle>
                      Organizational Psychology Specialist
                    </AuthorTitle>
                  </AuthorInfo>
                </AuthorSection>
              )}

              <Section>
                <SectionTitle>About This Survey</SectionTitle>
                <SectionText>{survey.description}</SectionText>
              </Section>

              <Section>
                <SectionTitle>When & How to Use</SectionTitle>
                <UsageSection>
                  <UsageTitle>Recommended Usage</UsageTitle>
                  <UsageText>{getUsageGuidance(survey.type)}</UsageText>
                </UsageSection>
              </Section>

              <Section>
                <SectionTitle>Question Preview</SectionTitle>
                <QuestionsList>
                  {survey.questions.slice(0, 5).map((question, index) => (
                    <QuestionItem key={index}>
                      <QuestionText>{question.question}</QuestionText>
                      <QuestionType>
                        {getQuestionTypeDisplay(question)}
                      </QuestionType>
                    </QuestionItem>
                  ))}
                  {survey.questions.length > 5 && (
                    <SectionText
                      style={{ textAlign: "center", fontStyle: "italic" }}
                    >
                      + {survey.questions.length - 5} more questions...
                    </SectionText>
                  )}
                </QuestionsList>
              </Section>
            </SheetContent>
          </>
        )}
      </SheetContainer>
    </SheetOverlay>
  );
};

export default SurveyDetailSheet; 